/**
 * The canvas's registry declaration.
 *
 * A hand-written manifest rather than an inlined `manifest.json`, because there
 * is no extension package to read one from -- Project Canvas is core. The three
 * identifiers below are not free choices: a shared `.canvas` document records
 * `documentType: 'canvas'`, `fileExtension: '.canvas'` and
 * `editorId: 'builtin.canvas'` when it is created on the desktop, and
 * `resolveSharedDocumentTypePresentation` matches on all three. A different
 * `extensionId` here would make every existing board resolve as an unsupported
 * document type in the browser.
 *
 * `.canvas` deliberately does not appear in `newFileMenu`. The catalog reports
 * an extension-kind descriptor as `sharedCreate: false` regardless, since
 * nothing in the browser seeds a new room from a manifest's default content, so
 * a menu entry would only advertise a creation path that does not exist.
 */

import { registerBrowserExtension, type BrowserExtensionManifest } from '@nimbalyst/web-editors/registry';

export const BUILTIN_CANVAS_EXTENSION_ID = 'builtin.canvas';
export const BUILTIN_CANVAS_COMPONENT = 'ProjectCanvasEditor';

export const BUILTIN_CANVAS_MANIFEST: BrowserExtensionManifest = {
  id: BUILTIN_CANVAS_EXTENSION_ID,
  name: 'Project Canvas',
  contributions: {
    customEditors: [
      {
        filePatterns: ['*.canvas'],
        displayName: 'Project Canvas',
        component: BUILTIN_CANVAS_COMPONENT,
        // Matches what `registerBuiltinCustomEditors.tsx` declares on the
        // desktop, verbatim in intent: "a `.canvas` file is JSON, so the host's
        // Monaco source view is a free and genuinely useful escape hatch when a
        // board will not open." A board on the web can degrade in five distinct
        // ways this console cannot fix -- a card pointing at a local file, an
        // unshared project, a pinned revision -- so the escape hatch is worth
        // more here than it is on the desktop, not less.
        supportsSourceMode: true,
        collaboration: { supported: true, documentType: 'canvas' },
      },
    ],
    fileIcons: { '*.canvas': 'dashboard' },
  },
  marketplace: { icon: 'dashboard' },
};

/**
 * Declares the manifest only. The board -- React Flow, the card tree, the
 * binding -- arrives with the lazy import the first time a `.canvas` document
 * is opened.
 */
export function registerBuiltinCanvasExtension(): void {
  registerBrowserExtension({
    manifest: BUILTIN_CANVAS_MANIFEST,
    load: () => import('./webCanvasBundle'),
  });
}
